import React, {useEffect} from 'react';
import {
  VariantType,
  SnackbarMessage,
  SnackbarProvider,
  useSnackbar,
  SnackbarAction,
  SnackbarKey,
} from 'notistack';
import {createRoot} from 'react-dom/client';
import {IconButton} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/*

This works weirdly because apollo and other parts of oneview are run outside of react.

So when we make a toast from these providers, we need to create a new instance of react per toast.

1) have a top level contain to hold any snackbar providers

2) on toast, insert a new html element with a unique id

3) create a react dom root attached to the new html element

4) generate the toast using a child component

5) tear it all back down once the user dismisses the snackbar
*/

const snackbarhelperContainer = document.getElementById('snackbarhelper')!;

const CloseButton: React.FC<{snackbarKey: SnackbarKey}> = ({snackbarKey}) => {
  const {closeSnackbar} = useSnackbar();
  return (
    <IconButton
      aria-label="Close this notification"
      color="inherit"
      onClick={() => closeSnackbar(snackbarKey)}
      size="small"
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );
};

const ToastInner: React.FC<{
  msg: SnackbarMessage;
  variant: VariantType;
  action?: SnackbarAction;
  onClosed: () => void;
}> = ({msg, variant, action, onClosed}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  useEffect(() => {
    enqueueSnackbar(String(msg), {
      variant,
      preventDuplicate: true,
      key: msg as any,
      persist: true,
      action: (key) => (
        <>
          {typeof action === 'function' ? action(key) : action}
          <CloseButton snackbarKey={key} />
        </>
      ),
      onClose: onClosed,
    });
    return () => {
      closeSnackbar();
    };
  }, []);
  return null;
};

let count = 0;
// used exclusively for apollo links and outside of any react
export const toast = (
  msg: SnackbarMessage,
  variant: VariantType,
  action?: SnackbarAction
) => {
  // withSnackbar has been deprecated
  // read more: https://notistack.com/migration
  const rootId = `snack-${(count += 1)}`;
  const newRoot = document.createElement('div');
  newRoot.id = rootId;
  snackbarhelperContainer.appendChild(newRoot);
  const root = createRoot(document.getElementById(rootId)!, {});

  root.render(
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
    >
      <ToastInner
        msg={msg}
        variant={variant}
        action={action}
        onClosed={() => setTimeout(() => root.unmount(), 300)}
      />
    </SnackbarProvider>
  );
};
